/**
 * Quiz Result Screen
 * Display quiz results with stats, achievements, and options to play again
 */

import { useEffect, useRef, useMemo, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Animated,
  Share,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import LottieView from "lottie-react-native";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { getLottieSource } from "@/lib/lottie-registry";
import { useAuth } from "@/providers/AuthProvider";
import { useQuiz, QuizSessionResult } from "@/modules/quiz";
import { supabase } from "@/lib/supabase/client";

export default function QuizResultScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ result?: string; mode?: string; quizId?: string }>();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, activeWeekend } = useAuth();

  const { resetQuiz } = useQuiz();
  const [notificationSent, setNotificationSent] = useState(false);
  const [canPlayAgain, setCanPlayAgain] = useState(true);
  const [playAgainReason, setPlayAgainReason] = useState<string | null>(null);

  // Parse result from params or use hook result
  const sessionResult: QuizSessionResult | null = useMemo(() => {
    if (params.result) {
      try {
        return JSON.parse(params.result);
      } catch {
        return null;
      }
    }
    return null;
  }, [params.result]);

  const isWeekendMode = params.mode === "weekend";

  /**
   * Send notification to team members (weekend mode only)
   */
  useEffect(() => {
    if (!sessionResult || !isWeekendMode || notificationSent || !activeProfile?.id) return;

    const sendTeamNotification = async () => {
      try {
        // Get user's team
        const { data: teamMember, error: teamError } = await supabase
          .from("team_members")
          .select("team_id, teams(name)")
          .eq("user_id", activeProfile.id)
          .single();

        if (teamError || !teamMember?.team_id) return;

        // Get team members (excluding current user)
        const { data: members, error: membersError } = await supabase
          .from("team_members")
          .select("user_id")
          .eq("team_id", teamMember.team_id)
          .neq("user_id", activeProfile.id);

        if (membersError || !members?.length) return;

        // Get user's name
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, display_name")
          .eq("id", activeProfile.id)
          .single();

        const userName = profile?.display_name || profile?.full_name || "Een teamlid";

        // Create notifications for each team member
        const notifications = members.map((member) => ({
          user_id: member.user_id,
          type: "quiz_completed",
          title: "🎯 Quiz Voltooid!",
          body: `${userName} heeft ${sessionResult.total_points} punten verdiend met de quiz! (${sessionResult.correct_answers}/${sessionResult.total_questions} correct)`,
          data: {
            quiz_score: sessionResult.total_points,
            correct_answers: sessionResult.correct_answers,
            total_questions: sessionResult.total_questions,
            accuracy: sessionResult.accuracy_percentage,
          },
          is_read: false,
        }));

        await supabase.from("notifications").insert(notifications);
        setNotificationSent(true);
      } catch (error) {
        console.error("Error sending team notification:", error);
      }
    };

    sendTeamNotification();
  }, [sessionResult, isWeekendMode, notificationSent, activeProfile?.id]);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const countAnim = useRef(new Animated.Value(0)).current;

  // Calculate performance grade
  const performanceGrade = useMemo(() => {
    if (!sessionResult) return { text: "", color: "", icon: "", message: "" };

    const accuracy = sessionResult.accuracy_percentage;

    if (accuracy >= 90) {
      return {
        text: "Uitstekend!",
        color: "#10B981",
        icon: "trophy",
        message: "Je bent een echte quizmaster!",
        lottie: "confetti",
      };
    } else if (accuracy >= 70) {
      return {
        text: "Goed gedaan!",
        color: "#3B82F6",
        icon: "thumbs-up",
        message: "Prima prestatie, ga zo door!",
        lottie: "cheers",
      };
    } else if (accuracy >= 50) {
      return {
        text: "Netjes!",
        color: "#F59E0B",
        icon: "happy",
        message: "Er is nog ruimte voor verbetering.",
        lottie: null,
      };
    } else {
      return {
        text: "Volgende keer beter!",
        color: "#EF4444",
        icon: "refresh",
        message: "Oefening baart kunst!",
        lottie: null,
      };
    }
  }, [sessionResult]);

  // Start animations on mount
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 80,
        friction: 10,
      }),
    ]).start();

    // Animate score count
    if (sessionResult) {
      Animated.timing(countAnim, {
        toValue: sessionResult.total_points,
        duration: 1500,
        useNativeDriver: false,
      }).start();
    }
  }, [sessionResult, fadeAnim, scaleAnim, countAnim]);

  /**
   * Check if user can play again (for weekend mode quizzes with restrictions)
   */
  useEffect(() => {
    const checkCanPlayAgain = async () => {
      // Free play mode - always allow replay
      if (!isWeekendMode || !params.quizId) {
        setCanPlayAgain(true);
        return;
      }

      try {
        // Fetch quiz settings
        const { data: quiz, error: quizError } = await supabase
          .from("quizzes")
          .select("*")
          .eq("id", params.quizId)
          .single();

        if (quizError || !quiz) {
          setCanPlayAgain(false);
          return;
        }

        // If unlimited plays, allow
        if (quiz.allow_unlimited_plays) {
          setCanPlayAgain(true);
          return;
        }

        // Get user's team for team-based quizzes
        let teamId: string | null = null;
        if (quiz.is_team_based && activeWeekend) {
          const { data: teamMember } = await supabase
            .from("team_members")
            .select("team_id, teams!inner(weekend_id)")
            .eq("user_id", activeProfile?.id || "")
            .eq("teams.weekend_id", activeWeekend.id)
            .single();
          
          teamId = teamMember?.team_id || null;
        }

        // Fetch completed sessions for this quiz
        const { data: sessions } = await supabase
          .from("game_sessions")
          .select("id, created_by, participants, points_awarded")
          .eq("quiz_id", params.quizId)
          .eq("status", "completed");

        const completedSessions = sessions || [];

        if (quiz.is_team_based && teamId) {
          // Team-based: check team plays
          const teamPlays = completedSessions.filter((s) =>
            s.participants?.some((p: any) => p.team_id === teamId)
          ).length;

          // Check for retry on zero
          if (quiz.allow_retry_on_zero) {
            const teamScoreTotal = completedSessions
              .filter((s) => s.participants?.some((p: any) => p.team_id === teamId))
              .reduce((sum, s) => sum + (s.points_awarded || 0), 0);
            
            if (teamScoreTotal === 0 && teamPlays < quiz.max_plays_per_team + 1) {
              setCanPlayAgain(true);
              setPlayAgainReason("Omdat je team 0 punten scoorde, mag er opnieuw gespeeld worden.");
              return;
            }
          }

          if (teamPlays >= quiz.max_plays_per_team) {
            setCanPlayAgain(false);
            setPlayAgainReason(`Je team heeft het maximum aantal pogingen (${quiz.max_plays_per_team}) bereikt.`);
            return;
          }
        } else {
          // Individual: check user plays
          const userPlays = completedSessions.filter(
            (s) => s.created_by === activeProfile?.id
          ).length;

          if (userPlays >= quiz.max_plays_per_user) {
            setCanPlayAgain(false);
            setPlayAgainReason(`Je hebt het maximum aantal pogingen (${quiz.max_plays_per_user}) bereikt.`);
            return;
          }
        }

        setCanPlayAgain(true);
      } catch (error) {
        console.error("Error checking play again:", error);
        setCanPlayAgain(true); // Default to allowing replay
      }
    };

    checkCanPlayAgain();
  }, [isWeekendMode, params.quizId, activeProfile?.id, activeWeekend]);

  /**
   * Handle play again
   */
  const handlePlayAgain = () => {
    resetQuiz();
    
    if (isWeekendMode && params.quizId) {
      // Weekend mode with specific quiz - play the same quiz again
      router.replace(`/(tabs)/games/quiz/play?quizId=${params.quizId}&mode=weekend`);
    } else if (isWeekendMode) {
      // Weekend mode without quiz ID - go to quiz selection
      router.replace("/(tabs)/games/quiz?mode=weekend");
    } else {
      // Free play mode - go to category selection
      router.replace("/(tabs)/games/quiz?mode=free");
    }
  };

  /**
   * Handle go to games
   */
  const handleGoToGames = () => {
    resetQuiz();
    router.replace("/(tabs)/games");
  };

  /**
   * Handle share result
   */
  const handleShareResult = async () => {
    if (!sessionResult) return;

    try {
      await Share.share({
        message: `🎯 Quiz Resultaat!\n\n✅ ${sessionResult.correct_answers}/${sessionResult.total_questions} correct\n🏆 ${sessionResult.total_points} punten\n📊 ${sessionResult.accuracy_percentage}% nauwkeurigheid\n\nSpeel mee in de Familie App!`,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  if (!sessionResult) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <Text style={{ color: colors.textSecondary }}>Geen resultaten beschikbaar</Text>
        <TouchableOpacity
          onPress={handleGoToGames}
          className="mt-4 px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary }}
        >
          <Text className="font-bold text-white">Terug naar Spellen</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Lottie Celebration */}
        {performanceGrade.lottie && (
          <View className="absolute top-0 left-0 right-0 h-72 pointer-events-none">
            <LottieView
              source={getLottieSource(performanceGrade.lottie) || getLottieSource("confetti")}
              autoPlay
              loop={false}
              style={{ flex: 1 }}
            />
          </View>
        )}

        {/* Header */}
        <Animated.View
          className="items-center pt-8 pb-6"
          style={{
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          }}
        >
          <View
            className="w-24 h-24 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${performanceGrade.color}20` }}
          >
            <Ionicons
              name={performanceGrade.icon as any}
              size={48}
              color={performanceGrade.color}
            />
          </View>

          <Text
            className="text-3xl font-bold mb-2"
            style={{ color: performanceGrade.color }}
          >
            {performanceGrade.text}
          </Text>

          <Text style={{ color: colors.textSecondary }}>
            {performanceGrade.message}
          </Text>
        </Animated.View>

        {/* Score Card */}
        <Animated.View
          className="mx-4 rounded-2xl p-6"
          style={{
            backgroundColor: colors.surface,
            opacity: fadeAnim,
          }}
        >
          <View className="items-center mb-6">
            <Text className="text-sm mb-1" style={{ color: colors.textSecondary }}>
              Totale Score
            </Text>
            <Animated.Text
              className="text-5xl font-bold"
              style={{ color: colors.primary }}
            >
              {sessionResult.total_points}
            </Animated.Text>
            <Text style={{ color: colors.textSecondary }}>punten</Text>
          </View>

          {/* Stats Grid */}
          <View className="flex-row flex-wrap">
            <StatItem
              icon="checkmark-circle"
              label="Correct"
              value={`${sessionResult.correct_answers}/${sessionResult.total_questions}`}
              color="#10B981"
              colors={colors}
            />
            <StatItem
              icon="analytics"
              label="Nauwkeurigheid"
              value={`${sessionResult.accuracy_percentage}%`}
              color="#3B82F6"
              colors={colors}
            />
            <StatItem
              icon="time"
              label="Gem. Tijd"
              value={`${Math.round(sessionResult.average_time_ms / 1000)}s`}
              color="#F59E0B"
              colors={colors}
            />
            <StatItem
              icon="flash"
              label="Snelste"
              value={`${Math.round(sessionResult.fastest_answer_ms / 1000)}s`}
              color="#8B5CF6"
              colors={colors}
            />
          </View>

          {/* Best Streak */}
          {sessionResult.best_streak > 1 && (
            <View
              className="mt-4 p-4 rounded-xl flex-row items-center"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons name="flame" size={24} color="#F59E0B" />
              <View className="ml-3">
                <Text className="font-bold" style={{ color: colors.text }}>
                  Beste Reeks: {sessionResult.best_streak} op rij
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  {sessionResult.best_streak >= 5
                    ? "Fantastisch! 🔥"
                    : "Goed bezig!"}
                </Text>
              </View>
            </View>
          )}
        </Animated.View>

        {/* Weekend Mode - Team Points */}
        {isWeekendMode && (
          <Animated.View
            className="mx-4 mt-4 p-4 rounded-2xl"
            style={{
              backgroundColor: "#8B5CF620",
              borderWidth: 2,
              borderColor: "#8B5CF6",
              opacity: fadeAnim,
            }}
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-full items-center justify-center"
                style={{ backgroundColor: "#8B5CF630" }}
              >
                <Ionicons name="trophy" size={24} color="#8B5CF6" />
              </View>
              <View className="ml-3 flex-1">
                <Text className="font-bold text-lg" style={{ color: "#8B5CF6" }}>
                  +{sessionResult.total_points} Team Punten
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Je teamleden zijn op de hoogte gebracht!
                </Text>
              </View>
            </View>
          </Animated.View>
        )}

        {/* Perfect Score Badge */}
        {sessionResult.accuracy_percentage === 100 && (
          <Animated.View
            className="mx-4 mt-4 p-4 rounded-2xl flex-row items-center"
            style={{
              backgroundColor: "#FFD70020",
              borderWidth: 2,
              borderColor: "#FFD700",
              opacity: fadeAnim,
            }}
          >
            <View
              className="w-12 h-12 rounded-full items-center justify-center"
              style={{ backgroundColor: "#FFD70030" }}
            >
              <Text className="text-2xl">🏆</Text>
            </View>
            <View className="ml-3 flex-1">
              <Text className="font-bold text-lg" style={{ color: "#B8860B" }}>
                Perfecte Score!
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Alle vragen goed beantwoord!
              </Text>
            </View>
          </Animated.View>
        )}

        <View className="h-8" />
      </ScrollView>

      {/* Action Buttons */}
      <View className="px-4 pb-4">
        {/* Share Button */}
        <TouchableOpacity
          onPress={handleShareResult}
          className="rounded-xl py-3 flex-row items-center justify-center mb-3"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
          accessibilityRole="button"
          accessibilityLabel="Deel resultaat"
        >
          <Ionicons name="share-outline" size={20} color={colors.text} />
          <Text className="ml-2 font-medium" style={{ color: colors.text }}>
            Deel Resultaat
          </Text>
        </TouchableOpacity>

        {/* Can't play again message */}
        {!canPlayAgain && playAgainReason && (
          <View
            className="mb-3 p-3 rounded-xl flex-row items-center"
            style={{ backgroundColor: "#F59E0B15" }}
          >
            <Ionicons name="information-circle" size={20} color="#F59E0B" />
            <Text className="ml-2 flex-1 text-sm" style={{ color: "#F59E0B" }}>
              {playAgainReason}
            </Text>
          </View>
        )}

        <View className="flex-row">
          {/* Back to Games */}
          <TouchableOpacity
            onPress={handleGoToGames}
            className={canPlayAgain ? "flex-1 mr-2" : "flex-1"}
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
              borderRadius: 12,
              paddingVertical: 16,
              alignItems: "center",
            }}
            accessibilityRole="button"
            accessibilityLabel="Terug naar spellen"
          >
            <Ionicons name="game-controller" size={22} color={colors.text} />
            <Text className="mt-1 text-sm font-medium" style={{ color: colors.text }}>
              Spellen
            </Text>
          </TouchableOpacity>

          {/* Play Again - only show if can play again */}
          {canPlayAgain && (
            <TouchableOpacity
              onPress={handlePlayAgain}
              className="flex-[2] ml-2 rounded-xl py-4 flex-row items-center justify-center"
              style={{ backgroundColor: colors.primary }}
              accessibilityRole="button"
              accessibilityLabel="Opnieuw spelen"
            >
              <Ionicons name="refresh" size={22} color="#FFFFFF" />
              <Text className="ml-2 font-bold text-white">Opnieuw Spelen</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
}

/**
 * Stat Item Component
 */
interface StatItemProps {
  icon: string;
  label: string;
  value: string;
  color: string;
  colors: ReturnType<typeof useThemeColors>;
}

function StatItem({ icon, label, value, color, colors }: StatItemProps) {
  return (
    <View className="w-1/2 p-2">
      <View
        className="rounded-xl p-3 flex-row items-center"
        style={{ backgroundColor: `${color}10` }}
      >
        <View
          className="w-10 h-10 rounded-lg items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Ionicons name={icon as any} size={20} color={color} />
        </View>
        <View className="ml-3">
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            {label}
          </Text>
          <Text className="font-bold" style={{ color: colors.text }}>
            {value}
          </Text>
        </View>
      </View>
    </View>
  );
}

